import React, { useState } from 'react';
import Box from '@Atoms/Box';
import HierarchyHeader from './HierarchyHeader';
import HierarchyRow from './HierarchyRow';
import StudentRow from './StudentRow';
import { getMasteryColor, getMasteryPercentage } from './utils';
import { Data, Skill, Lesson, Standard, Section } from './types';
import SetThresholdRange from '@components/Component/LearnosityExams/contexts/HighlightedScore/SetThresholdRange';
import { useHighlightedScore } from '@components/Component/LearnosityExams/contexts/HighlightedScore/HighlightedScoreContext';

export default function DistrictReport({
  data,
  sectionReport,
}: {
  data: Data;
  sectionReport?: (section: Section) => React.ReactNode;
}) {
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Get threshold values from context
  const { lowThreshold, highThreshold, getLowColor, getHighColor, getDefaultColor } =
    useHighlightedScore();

  const toggleExpand = (
    id: string,
    expanded: Set<string>,
    setExpanded: (s: Set<string>) => void,
  ) => {
    const newExpanded = new Set(expanded);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpanded(newExpanded);
  };

  // Defensive check: ensure data and organizations exist
  if (!data || !data.organizations || !Array.isArray(data.organizations)) {
    return null;
  }

  return (
    <>
      <SetThresholdRange />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125, width: '100%' }}>
        {/* Main Header */}
        <HierarchyHeader
          label="Organization"
          tooltipTitle="Results by organization"
          bgColor="action.hover"
          showScoreLegend={true}
        />

        {/* Organizations */}
        {data.organizations.map((org) => (
          <Box
            key={org.org_id}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Box
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
                transition: 'all 0.2s',
              }}
            >
              <HierarchyRow
                id={org.org_id}
                name={org.org_name}
                studentCount={org.student_count}
                scorePercentage={getMasteryPercentage(org.aggregated_mastery)}
                isExpanded={expandedOrgs.has(org.org_id)}
                onToggle={() => toggleExpand(org.org_id, expandedOrgs, setExpandedOrgs)}
                iconSize="1.125rem"
                fontSize="0.875rem"
                barColor={getMasteryColor(
                  org.aggregated_mastery,
                  lowThreshold,
                  highThreshold,
                  getLowColor,
                  getHighColor,
                  getDefaultColor,
                )}
                barHeight={24}
                showBorder={false}
              />
            </Box>

            {/* Courses */}
            {expandedOrgs.has(org.org_id) && (
              <Box sx={{ bgcolor: 'background.default' }}>
                <HierarchyHeader
                  label="Course"
                  leftMargin={4}
                  tooltipTitle="Results by course"
                />

                {(org.courses || []).map((course) => (
                  <Box key={course.course_id}>
                    <HierarchyRow
                      id={course.course_id}
                      name={course.course_name}
                      studentCount={course.student_count}
                      scorePercentage={getMasteryPercentage(course.aggregated_mastery)}
                      isExpanded={expandedCourses.has(course.course_id)}
                      onToggle={() =>
                        toggleExpand(
                          course.course_id,
                          expandedCourses,
                          setExpandedCourses,
                        )
                      }
                      leftMargin={4}
                      barColor={getMasteryColor(
                        course.aggregated_mastery,
                        lowThreshold,
                        highThreshold,
                        getLowColor,
                        getHighColor,
                        getDefaultColor,
                      )}
                    />

                    {/* Sections */}
                    {expandedCourses.has(course.course_id) && (
                      <Box sx={{ bgcolor: 'background.default' }}>
                        <HierarchyHeader
                          label="Section"
                          leftMargin={8}
                          tooltipTitle="Results by section"
                          bgColor="grey.100"
                        />

                        {(course.sections || []).map((section) => (
                          <Box key={section.section_id}>
                            <HierarchyRow
                              id={section.section_id}
                              name={section.section_name}
                              studentCount={section.student_count}
                              scorePercentage={getMasteryPercentage(
                                section.aggregated_mastery,
                              )}
                              isExpanded={expandedSections.has(section.section_id)}
                              onToggle={() =>
                                toggleExpand(
                                  section.section_id,
                                  expandedSections,
                                  setExpandedSections,
                                )
                              }
                              leftMargin={8}
                              iconSize="0.875rem"
                              fontSize="0.75rem"
                              barColor={getMasteryColor(
                                section.aggregated_mastery,
                                lowThreshold,
                                highThreshold,
                                getLowColor,
                                getHighColor,
                                getDefaultColor,
                              )}
                            />

                            {/* Skills, Lessons, or Standards */}
                            {expandedSections.has(section.section_id) &&
                              (() => {
                                // If custom section report is provided, use it
                                if (sectionReport) {
                                  return sectionReport(section);
                                }

                                // Otherwise, use default rendering
                                // Determine which type of items exist
                                const hasSkills =
                                  section.skills && section.skills.length > 0;
                                const hasLessons =
                                  section.lessons && section.lessons.length > 0;
                                const hasStandards =
                                  section.standards && section.standards.length > 0;

                                // Determine the item type and label
                                let items: Array<Skill | Lesson | Standard> = [];
                                let itemLabel = '';
                                let tooltipTitle = '';

                                if (hasSkills) {
                                  items = section.skills || [];
                                  itemLabel = 'Skill';
                                  tooltipTitle = 'Results by skill';
                                } else if (hasLessons) {
                                  items = section.lessons || [];
                                  itemLabel = 'Lesson';
                                  tooltipTitle = 'Results by lesson';
                                } else if (hasStandards) {
                                  items = section.standards || [];
                                  itemLabel = 'Standard';
                                  tooltipTitle = 'Results by standard';
                                }

                                // If no items exist, don't render anything
                                if (items.length === 0) {
                                  return null;
                                }

                                return (
                                  <Box>
                                    <HierarchyHeader
                                      label={itemLabel}
                                      leftMargin={12}
                                      tooltipTitle={tooltipTitle}
                                    />

                                    {items.map((item) => {
                                      // Extract common properties from any item type
                                      const itemId =
                                        'skill_id' in item
                                          ? item.skill_id
                                          : 'lesson_id' in item
                                          ? item.lesson_id
                                          : item.standard_id;
                                      const itemName =
                                        'skill_name' in item
                                          ? item.skill_name
                                          : 'lesson_name' in item
                                          ? item.lesson_name
                                          : item.standard_name;

                                      return (
                                        <Box key={itemId}>
                                          <HierarchyRow
                                            id={itemId}
                                            name={itemName}
                                            studentCount={item.student_count}
                                            scorePercentage={getMasteryPercentage(
                                              item.mastery,
                                            )}
                                            isExpanded={expandedItems.has(itemId)}
                                            onToggle={() =>
                                              toggleExpand(
                                                itemId,
                                                expandedItems,
                                                setExpandedItems,
                                              )
                                            }
                                            leftMargin={12}
                                            iconSize="0.875rem"
                                            fontSize="0.75rem"
                                            barColor={getMasteryColor(
                                              item.mastery,
                                              lowThreshold,
                                              highThreshold,
                                              getLowColor,
                                              getHighColor,
                                              getDefaultColor,
                                            )}
                                          />

                                          {/* Students */}
                                          {expandedItems.has(itemId) && (
                                            <Box sx={{ bgcolor: 'background.default' }}>
                                              <HierarchyHeader
                                                label="Student"
                                                leftMargin={16}
                                                tooltipTitle="Student results"
                                                secondColumnLabel="Score/Max Score"
                                              />

                                              {(item.students || []).map((student) => (
                                                <StudentRow
                                                  key={student.student_id}
                                                  studentName={student.student_name}
                                                  score={student.score}
                                                  maxScore={student.max_score}
                                                  percentage={student.percentage}
                                                  barColor={getMasteryColor(
                                                    student.percentage,
                                                    lowThreshold,
                                                    highThreshold,
                                                    getLowColor,
                                                    getHighColor,
                                                    getDefaultColor,
                                                  )}
                                                />
                                              ))}
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                );
                              })()}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </>
  );
}
